import React, { useState, useEffect } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Stack,
	Tabs,
	Tab,
	Box,
	FormControlLabel,
	Switch,
	Typography
} from "@mui/material";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import {
	getBusinessProfile,
	updateBusinessProfile,
	getConversationalAutomation,
	setConversationalAutomation,
	getConversationAnalytics
} from "../../services/wabaFeatures";
import { isFeatureOn, WABA_FLAGS } from "../../helpers/featureFlags";
import WabaCatalogTab from "./WabaCatalogTab";

// Configuração da conexão WABA oficial: perfil do negócio + saudação/atalhos (ice breakers).
const WabaConfigModal = ({ open, onClose, whatsApp }) => {
	const whatsappId = whatsApp?.id;
	const canProfile = isFeatureOn(WABA_FLAGS.BUSINESS_PROFILE);
	const canIce = isFeatureOn(WABA_FLAGS.ICE_BREAKERS);
	const canAnalytics = isFeatureOn(WABA_FLAGS.ANALYTICS);
	const canCatalog = isFeatureOn(WABA_FLAGS.CATALOG);
	const [analytics, setAnalytics] = useState(null);

	const [tab, setTab] = useState(0);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	const [profile, setProfile] = useState({ about: "", description: "", email: "", address: "", websites: "" });
	const [enableWelcome, setEnableWelcome] = useState(false);
	const [ice, setIce] = useState(["", "", "", ""]);

	useEffect(() => {
		if (!open || !whatsappId) return;
		const load = async () => {
			setLoading(true);
			try {
				if (canProfile) {
					const { data } = await getBusinessProfile(whatsappId);
					// resposta: { ok, data: { data: [ { about, ... } ] } }
					const p = (data?.data?.data && data.data.data[0]) || {};
					setProfile({
						about: p.about || "",
						description: p.description || "",
						email: p.email || "",
						address: p.address || "",
						websites: (p.websites || []).join(", ")
					});
				}
				if (canIce) {
					const { data } = await getConversationalAutomation(whatsappId);
					const ca = data?.data || {};
					setEnableWelcome(!!ca.enable_welcome_message);
					const prompts = (ca.prompts || []).map(p => (typeof p === "string" ? p : p.prompt_text || ""));
					setIce([0, 1, 2, 3].map(i => prompts[i] || ""));
				}
				if (canAnalytics) {
					const end = Math.floor(Date.now() / 1000);
					const start = end - 30 * 24 * 3600;
					const { data } = await getConversationAnalytics(whatsappId, { start, end, granularity: "DAILY" });
					const dp = (data?.data?.analytics?.data_points) || [];
					setAnalytics({
						sent: dp.reduce((a, p) => a + (p.sent || 0), 0),
						delivered: dp.reduce((a, p) => a + (p.delivered || 0), 0),
						days: dp.length
					});
				}
			} catch (err) {
				toastError(err);
			} finally {
				setLoading(false);
			}
		};
		load();
		setTab(canProfile ? 0 : 1);
	}, [open, whatsappId, canProfile, canIce]);

	const setP = key => e => setProfile(p => ({ ...p, [key]: e.target.value }));
	const setIceAt = i => e => setIce(arr => arr.map((v, idx) => (idx === i ? e.target.value : v)));

	const handleSaveProfile = async () => {
		setSaving(true);
		try {
			await updateBusinessProfile(whatsappId, {
				about: profile.about,
				description: profile.description,
				email: profile.email,
				address: profile.address,
				websites: profile.websites
					? profile.websites.split(",").map(s => s.trim()).filter(Boolean)
					: []
			});
			toast.success("Perfil atualizado!");
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const handleSaveIce = async () => {
		setSaving(true);
		try {
			await setConversationalAutomation(whatsappId, {
				enableWelcome,
				iceBreakers: ice.map(s => s.trim()).filter(Boolean)
			});
			toast.success("Saudação e atalhos atualizados!");
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Configuração WhatsApp Oficial — {whatsApp?.name}</DialogTitle>
			<DialogContent dividers>
				<Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
					{canProfile && <Tab label="Perfil do negócio" value={0} />}
					{canIce && <Tab label="Saudação e atalhos" value={1} />}
					{canAnalytics && <Tab label="Métricas" value={2} />}
					{canCatalog && <Tab label="Catálogo" value={3} />}
				</Tabs>

				{loading && <Typography sx={{ py: 2 }}>Carregando…</Typography>}

				{!loading && tab === 0 && canProfile && (
					<Stack spacing={2}>
						<TextField label="Descrição" value={profile.description} onChange={setP("description")} fullWidth />
						<TextField label="Sobre (status)" value={profile.about} onChange={setP("about")} fullWidth />
						<TextField label="E-mail" value={profile.email} onChange={setP("email")} fullWidth />
						<TextField label="Endereço" value={profile.address} onChange={setP("address")} fullWidth />
						<TextField label="Sites (separados por vírgula)" value={profile.websites} onChange={setP("websites")} fullWidth />
					</Stack>
				)}

				{!loading && tab === 1 && canIce && (
					<Stack spacing={2}>
						<FormControlLabel
							control={<Switch checked={enableWelcome} onChange={e => setEnableWelcome(e.target.checked)} />}
							label="Ativar mensagem de boas-vindas"
						/>
						<Typography variant="body2" color="textSecondary">
							Atalhos (ice breakers) — até 4, aparecem quando o cliente abre a conversa:
						</Typography>
						{ice.map((v, i) => (
							<TextField
								key={i}
								label={`Atalho ${i + 1}`}
								value={v}
								onChange={setIceAt(i)}
								fullWidth
								inputProps={{ maxLength: 80 }}
							/>
						))}
					</Stack>
				)}
				{tab === 3 && canCatalog && <WabaCatalogTab whatsappId={whatsappId} />}

				{!loading && tab === 2 && canAnalytics && (
					<Stack spacing={1}>
						<Typography variant="subtitle2">Últimos 30 dias</Typography>
						<Box sx={{ display: "flex", gap: 3 }}>
							<Box>
								<Typography variant="h5">{analytics?.sent ?? 0}</Typography>
								<Typography variant="body2" color="textSecondary">Enviadas</Typography>
							</Box>
							<Box>
								<Typography variant="h5">{analytics?.delivered ?? 0}</Typography>
								<Typography variant="body2" color="textSecondary">Entregues</Typography>
							</Box>
						</Box>
						<Typography variant="caption" color="textSecondary">
							Fonte: WhatsApp Cloud API (mensageria).
						</Typography>
					</Stack>
				)}
			</DialogContent>
			<DialogActions sx={{ p: 2, gap: 1 }}>
				<Button onClick={onClose} disabled={saving}>Fechar</Button>
				{tab === 0 && canProfile && (
					<Button onClick={handleSaveProfile} variant="contained" disabled={saving || loading}>
						{saving ? "Salvando..." : "Salvar perfil"}
					</Button>
				)}
				{tab === 1 && canIce && (
					<Button onClick={handleSaveIce} variant="contained" disabled={saving || loading}>
						{saving ? "Salvando..." : "Salvar saudação"}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default WabaConfigModal;
